/* eslint-disable */
import { useState, useRef } from 'react';
// @mui
import { LoadingButton } from '@mui/lab';
import { useNavigate } from 'react-router-dom';
import { Container, Typography, Box, CircularProgress, Select, MenuItem, FormControl, InputLabel, Button } from '@mui/material';
import { Helmet } from 'react-helmet-async';
import toast from 'react-hot-toast';
import DownloadIcon from '@mui/icons-material/Download';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import html2canvas from 'html2canvas';
import { DailyQuotes } from '../../components/daily-quotes';
import { DownloadReportDialog } from '../../components/dialog';
import { getUserDetails, downloadExcelReport } from '../../utils';
import { useQueryGetTotalUsers } from '../../hooks/react-query/useData';
import { useQueryGetTodayQuote } from '../../hooks/react-query/quotes';
import { AnalyticsDashboard } from '../../components/analytics';
import { useQueryGetSchoolsList } from '../../hooks/react-query/schools';

// ----------------------------------------------------------------------

export default function AdminDashboard() {
  const navigate = useNavigate();
  const userDetails = getUserDetails();

  const userRole = userDetails?.role?.toLowerCase();

  const { data: todayQuote } = useQueryGetTodayQuote();
  const [isReportLoading, setIsReportLoading] = useState(false);
  const [openDialogReportDialog, setOpenDialogReportDialog] = useState(false);
  const [reportData, setReportData] = useState({});
  const [selectedSchoolId, setSelectedSchoolId] = useState('');
  const [isDownloadingSnapshot, setIsDownloadingSnapshot] = useState(false);
  const dashboardRef = useRef(null);

  // Fetch schools list for partnership dropdown (admin only)
  const { data: schoolsList } = useQueryGetSchoolsList(
    { token: userDetails?.token, role: userDetails?.role },
    navigate,
    toast
  );

  const { data, isFetching: isLoading } = useQueryGetTotalUsers(
    userDetails?.token,
    navigate,
    userDetails?.role,
    selectedSchoolId
  );

  const searchAdminReport = () => {
    if (Object.keys(reportData).length < 2) {
      toast.error('Both end and start date required');
      return;
    }

    if (reportData.endDate < reportData.startDate) {
      toast.error('End Date must be after start date');
      return;
    }
    setIsReportLoading(true);
    downloadExcelReport(
      toast,
      setIsReportLoading,
      userDetails?.token,
      setOpenDialogReportDialog,
      reportData,
      setReportData,
      selectedSchoolId // Pass schoolId to filter the report
    );
  };

  const handleDownloadSnapshot = async () => {
    if (!dashboardRef.current) return;

    setIsDownloadingSnapshot(true);
    try {
      // Get the current theme mode
      const isDarkMode = document.documentElement.getAttribute('data-theme') === 'dark' || 
                        document.body.classList.contains('dark');
      
      // Capture the dashboard
      const canvas = await html2canvas(dashboardRef.current, {
        backgroundColor: isDarkMode ? '#161622' : '#ffffff',
        scale: 2,
        logging: false,
        useCORS: true,
        allowTaint: true,
      });

      // Convert canvas to blob and download
      canvas.toBlob((blob) => {
        if (blob) {
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `dashboard-snapshot-${new Date().toISOString().split('T')[0]}.png`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        }
      }, 'image/png');
    } catch (error) {
      console.error('Error downloading snapshot:', error);
      toast.error('Failed to download snapshot. Please try again.');
    } finally {
      setIsDownloadingSnapshot(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Edifying Teachers | Dashboard</title>
      </Helmet>

      <Container maxWidth="xl" sx={{ p: '0 !important' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexDirection: { xs: 'column', md: 'row' },
            mb: { xs: '20px', md: 'initial' },
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="mainTitles" sx={{ mb: '5px', color: 'text.titles', display: 'block' }}>
              Welcome!
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            {/* Partnership Site Filter - Only for Admin */}
            {(userRole === 'admin') && (
              <FormControl sx={{ minWidth: 200 }} size="small">
                <InputLabel id="partnership-site-label">Partnership Site</InputLabel>
                <Select
                  labelId="partnership-site-label"
                  id="partnership-site-select"
                  value={selectedSchoolId}
                  label="Partnership Site"
                  onChange={(e) => setSelectedSchoolId(e.target.value)}
                  sx={{ bgcolor: 'background.paper' }}
                >
                  <MenuItem value="">All Partnership Sites</MenuItem>
                  {schoolsList?.data?.data?.length > 0 && schoolsList.data.data.map((school) => (
                    <MenuItem key={school._id} value={school._id}>
                      {school.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            
            <LoadingButton
              variant="contained"
              color="main"
              sx={{ height: '40px', width: 'fit-content' }}
              onClick={() => setOpenDialogReportDialog(true)}
            >
              Export Report
            </LoadingButton>
            <LoadingButton
              variant="contained"
              color="main"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadSnapshot}
              loading={isDownloadingSnapshot}
              sx={{ height: '40px', width: 'fit-content' }}
            >
              Download Snapshot
            </LoadingButton>
          </Box>
        </Box>

        {todayQuote && <DailyQuotes quote={todayQuote} />}

        {isLoading && (
          <Box sx={{ textAlign: 'center', my: '20px' }}>
            <CircularProgress />
          </Box>
        )}

        <Box sx={{ mt: '10px' }} ref={dashboardRef}>
          <AnalyticsDashboard data={data} isLoading={isLoading} />
        </Box>

        <DownloadReportDialog
          open={openDialogReportDialog}
          handleClose={() => setOpenDialogReportDialog(false)}
          setReportData={setReportData}
          reportData={reportData}
          searchReport={searchAdminReport}
          loading={isReportLoading}
        />
      </Container>
    </>
  );
}
